import { EmptyState } from "@cntrl/ui";
import { useRelease, useSmartLinkStats } from "../../app/hooks/useRelease.js";
import { daysToRelease, formatShortDate } from "./releaseFormat.js";
import sec from "./section.module.scss";
import styles from "./ReleaseMobileSummary.module.scss";

/**
 * The mobile READ-ONLY face of a release (ART-153 pass two). Spec §3: "Releases … mobile = catalogue +
 * per-release STATS, no editor." The command-center builder is desktop-only, so under `MobileShell`
 * {@link ReleaseDetailScreen} renders THIS below the (already read-only) hero instead of the section strip +
 * editor `<Outlet/>` + pager. It is the SAME KPI row the desktop Overview leads with — days to release,
 * smart-link 30d visits, assets progress, checklist — read from the already-cached `useRelease` /
 * `useSmartLinkStats` (no writes, no section forms), plus a plain "edit on desktop" notice.
 *
 * The `null → —` contract is kept exactly as the desktop Overview keeps it: an unavailable read is an em
 * dash, never a fabricated 0.
 */
export function ReleaseMobileSummary({ releaseId }: { releaseId: string }) {
  const q = useRelease(releaseId);
  const stats = useSmartLinkStats(releaseId);
  const overview = q.data?.data?.overview;
  const toDo = q.data?.data?.toDo;
  const assets = q.data?.data?.assets?.progress;
  const smartLink = q.data?.data?.smartLink;

  const days = daysToRelease(overview?.releaseDate);
  const total = toDo?.total ?? 0;
  const checklistPct = total > 0 ? Math.round(((toDo?.doneCount ?? 0) / total) * 100) : 0;
  const toGo = assets ? Math.max(0, (assets.total ?? 0) - (assets.added ?? 0)) : 0;

  return (
    <div className={styles.wrap}>
      <div className={sec.kpiRow}>
        <Kpi
          label="Days to release"
          value={days === null ? "—" : String(Math.abs(days))}
          sub={formatShortDate(overview?.releaseDate) || "No date"}
        />
        <Kpi
          label="Smart-link visits 30d"
          value={stats.isError || stats.data?.visits30d == null ? "—" : String(stats.data.visits30d)}
          sub={smartLink?.slug ? "public page" : "no link"}
        />
        <Kpi
          label="Assets in"
          value={assets ? `${assets.added ?? 0} / ${assets.total ?? 0}` : "—"}
          sub={assets ? `${toGo} to go` : ""}
        />
        <Kpi label="Checklist done" value={`${toDo?.doneCount ?? 0} / ${total}`} sub={`${checklistPct}% complete`} />
      </div>

      <EmptyState
        title="Editing is on desktop"
        description="Open this release on a larger screen to edit its details, assets, budget, timeline and smart link."
      />
    </div>
  );
}

// A read-only KPI tile, identical to the desktop Overview's — the shared `section.module.scss` classes so the
// two never drift.
function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className={sec.kpi}>
      <span className={sec.kpiLabel}>{label}</span>
      <span className={sec.kpiVal}>{value}</span>
      {sub && <span className={sec.kpiSub}>{sub}</span>}
    </div>
  );
}
